import React, { useMemo, useState } from 'react';
import { IotController } from '../../store/models/controllers';
import { DEFAULT_INPUT_CLASS } from '../InputControl/constants';

type ControllerSearchSelectProps = {
  controllers: IotController[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
};

const ControllerSearchSelect: React.FC<ControllerSearchSelectProps> = ({
  controllers,
  selectedIds,
  onChange,
  multiple = false,
}) => {
  const [search, setSearch] = useState('');

  const filteredControllers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return controllers;

    return controllers.filter((controller) => {
      const haystack = `${controller.name} ${controller.location?.name ?? ''} ${controller.serial_number}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [controllers, search]);

  const toggleController = (id: string) => {
    if (multiple) {
      const isSelected = selectedIds.includes(id);
      onChange(isSelected ? selectedIds.filter((item) => item !== id) : [...selectedIds, id]);
    } else {
      onChange([id]);
    }
  };

  return (
    <div className="flex w-full flex-col gap-2.5">
      <input
        type="text"
        className={DEFAULT_INPUT_CLASS}
        placeholder="Search controllers by name, client or serial number..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className="max-h-64 overflow-y-auto rounded border border-stroke dark:border-strokedark">
        {filteredControllers.length === 0 && (
          <p className="p-4 text-sm text-slate-gray">No controllers found.</p>
        )}
        {filteredControllers.map((controller) => {
          const isSelected = selectedIds.includes(controller.id);
          return (
            <label
              key={controller.id}
              className={`flex cursor-pointer items-center gap-3 border-b border-stroke px-4 py-2 last:border-b-0 dark:border-strokedark ${
                isSelected ? 'bg-primary/10' : ''
              }`}
            >
              <input
                type={multiple ? 'checkbox' : 'radio'}
                checked={isSelected}
                onChange={() => toggleController(controller.id)}
              />
              <span className="flex flex-col">
                <span className="font-medium text-black dark:text-white">{controller.name}</span>
                <span className="text-xs text-slate-gray">
                  {controller.location?.name ?? 'No client'} &middot; {controller.serial_number}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default ControllerSearchSelect;
